import { c as _c } from "react/compiler-runtime";
import { ALLOWED_MEDIA, ResponsiveImage } from '@/app/components/media/app-image';
import { SpartanProductCustomizeBufferedImage } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/buffered-image';
import { PREVIEW_IMAGE_PARAMS } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/preview-carousel/constants';
import type { SpartanProductCustomizePreviewThumbnailsProps } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/thumbnails/types';
import { getPreviewObjectFit, isVehiclePreviewView } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/utils';
import { CUSTOMIZE_CONTROLS_REVEAL_OFFSET } from '@/app/lcv/components/sections/spartan/products/customize/constants';
import styles from '@/app/lcv/components/sections/spartan/products/customize/styles.module.css';
import { SPARTAN_PRODUCT_CUSTOMIZE_DATA_GA, SPARTAN_PRODUCT_CUSTOMIZE_TRACKING } from '@/app/lcv/components/sections/spartan/products/customize/tracking';
import type { SpartanCustomizePreviewView } from '@/app/lcv/components/sections/spartan/products/customize/types';
import { SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/icon-button';
import { getSpartanRevealGroupProps, getSpartanRevealItemProps, SPARTAN_REVEAL_PRESET, SPARTAN_REVEAL_ROLE } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { SPARTAN_PRODUCTS_EVENT_NAME } from '@/app/lcv/components/sections/spartan/products/shared/tracking';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
const THUMBNAIL_IMAGE_SIZES = '(min-width: 1040px) 176px, (min-width: 1024px) calc((100vw - 512px) / 3), (min-width: 576px) 176px, calc((100vw - 48px) / 3)';
const THUMBNAIL_MOBILE_WIDTHS = [256, 384] as const;
const THUMBNAIL_DESKTOP_WIDTHS = [384] as const;
const IMAGE_PRELOAD_OFFSET = '100% 0px';
const THUMBNAIL_REVEAL_STAGGER_MS = 40;
export const SpartanProductCustomizePreviewThumbnails = t0 => {
  const $ = _c(34);
  const {
    options,
    pendingOptions,
    previewTone: t1,
    isDarkStudio: t2,
    activeView,
    onSelectView,
    onImageReady,
    onImageError
  } = t0;
  const previewTone = t1 === undefined ? "light" : t1;
  const isDarkStudio = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== activeView || $[1] !== onSelectView || $[2] !== options) {
    t3 = view => {
      if (view === activeView) {
        return;
      }
      if (!options.some(option => option.view === view && option.src)) {
        return;
      }
      onSelectView(view);
    };
    $[0] = activeView;
    $[1] = onSelectView;
    $[2] = options;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleSelectView = t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.previewNav
    });
    t5 = getSpartanRevealGroupProps(SPARTAN_REVEAL_PRESET.content, {
      scrollOffset: CUSTOMIZE_CONTROLS_REVEAL_OFFSET
    });
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = createDataAttributes({
      test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.thumbnails
    });
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== activeView || $[8] !== handleSelectView || $[9] !== isDarkStudio || $[10] !== onImageError || $[11] !== onImageReady || $[12] !== options || $[13] !== pendingOptions || $[14] !== previewTone) {
    let t8;
    if ($[16] !== activeView || $[17] !== handleSelectView || $[18] !== isDarkStudio || $[19] !== onImageError || $[20] !== onImageReady || $[21] !== options.length || $[22] !== pendingOptions || $[23] !== previewTone) {
      t8 = (option_0, index) => {
        const isSelected = option_0.view === activeView;
        const isVehicleView = isVehiclePreviewView(option_0.view);
        const hasImage = !!option_0.src;
        const isSurfaceToneHeld = pendingOptions.some(pendingOption => pendingOption.view === option_0.view && pendingOption.src !== option_0.src);
        return <li key={option_0.view} className={cn("aspect-16-9 col-span-2 flex max-w-44 min-w-0 items-center justify-center", options.length === 2 && (index === 0 ? "col-start-2" : "col-start-4"))} {...getSpartanRevealItemProps({
          order: index,
          role: SPARTAN_REVEAL_ROLE.galleryThumbnail,
          staggerMs: THUMBNAIL_REVEAL_STAGGER_MS
        })}><button type="button" className={cn("relative block w-full max-w-44 overflow-hidden rounded-md border-2 p-0.5 transition-[border-color] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-800 motion-reduce:transition-none", SPARTAN_PRODUCT_OUTLINE_NONE_CLASS_NAME, isSelected ? cn("cursor-default", isDarkStudio ? "border-white" : "border-gray-800") : cn("cursor-pointer border-transparent", isDarkStudio ? "hover:border-white focus-visible:border-white" : "hover:border-gray-800 focus-visible:border-gray-800"))} onClick={() => handleSelectView(option_0.view)} disabled={!hasImage} aria-pressed={isSelected} aria-label={option_0.label} {...createDataAttributes({
            test: SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.thumbnail(option_0.view),
            active: isSelected ? "true" : null
          })} {...createDataGA({
            eventName: SPARTAN_PRODUCTS_EVENT_NAME,
            group: SPARTAN_PRODUCT_CUSTOMIZE_DATA_GA.section,
            click: true,
            text: option_0.label,
            value: SPARTAN_PRODUCT_CUSTOMIZE_DATA_GA.thumbnail
          })}>{hasImage ? <SpartanProductCustomizeBufferedImage src={option_0.src} alt={option_0.label} width={320} height={180} sizes={THUMBNAIL_IMAGE_SIZES} mobileWidths={THUMBNAIL_MOBILE_WIDTHS} desktopWidths={THUMBNAIL_DESKTOP_WIDTHS} params={PREVIEW_IMAGE_PARAMS} objectFit={getPreviewObjectFit(option_0.view)} className="aspect-16-9 w-full overflow-hidden rounded-sm" surfaceClassName={isVehicleView ? styles.previewSurface : undefined} surfaceTone={isVehicleView ? previewTone : undefined} fade={false} decoding="sync" loading="lazy" preloadOffset={IMAGE_PRELOAD_OFFSET} onImageLoad={() => onImageReady(option_0)} onImageError={() => onImageError(option_0)} swapEffect="surfaceCrossfade" holdSurfaceTone={isSurfaceToneHeld} testId={SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.thumbnailImage(option_0.view)} /> : <div className={cn(styles.previewSurface, "aspect-16-9 w-full overflow-hidden rounded-sm")} data-preview-tone={isVehicleView ? previewTone : undefined} aria-hidden="true" />}</button></li>;
      };
      $[16] = activeView;
      $[17] = handleSelectView;
      $[18] = isDarkStudio;
      $[19] = onImageError;
      $[20] = onImageReady;
      $[21] = options.length;
      $[22] = pendingOptions;
      $[23] = previewTone;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    t7 = options.map(t8);
    $[7] = activeView;
    $[8] = handleSelectView;
    $[9] = isDarkStudio;
    $[10] = onImageError;
    $[11] = onImageReady;
    $[12] = options;
    $[13] = pendingOptions;
    $[14] = previewTone;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[25] !== t7) {
    t8 = <ul className="mx-auto grid w-full max-w-140 min-w-0 grid-cols-6 items-center gap-2 self-center md:gap-4" {...t6}>{t7}</ul>;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== onImageError || $[28] !== onImageReady || $[29] !== pendingOptions) {
    t9 = !!pendingOptions.length && <div aria-hidden="true" className="pointer-events-none h-0 w-0 overflow-hidden opacity-0">{pendingOptions.map(option_1 => <ResponsiveImage key={`${option_1.view}:${option_1.src}`} mobileSrc={{
        src: option_1.src,
        media: ALLOWED_MEDIA.lgDown,
        sizes: THUMBNAIL_IMAGE_SIZES,
        widths: THUMBNAIL_MOBILE_WIDTHS,
        params: PREVIEW_IMAGE_PARAMS
      }} desktopSrc={{
        src: option_1.src,
        media: ALLOWED_MEDIA.lgUp,
        sizes: THUMBNAIL_IMAGE_SIZES,
        widths: THUMBNAIL_DESKTOP_WIDTHS,
        params: PREVIEW_IMAGE_PARAMS
      }} alt="" width={320} height={180} loading="eager" fetchPriority="low" objectFit={getPreviewObjectFit(option_1.view)} className="aspect-16-9 w-full select-none" fade={false} onLoad={() => onImageReady(option_1)} onError={() => onImageError(option_1)} testId={`${SPARTAN_PRODUCT_CUSTOMIZE_TRACKING.thumbnailImage(option_1.view)}-preload`} />)}</div>;
    $[27] = onImageError;
    $[28] = onImageReady;
    $[29] = pendingOptions;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t8 || $[32] !== t9) {
    t10 = <div className="order-2 flex min-w-0 items-center justify-center lg:col-span-2 lg:row-start-2 lg:min-h-30" {...t4} {...t5}>{t8}{t9}</div>;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  return t10;
};